import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, AlertCircle, Info, Lightbulb } from 'lucide-react';
import '@/styles/eliteDesignSystem.css';

/**
 * MarkdownContentRenderer
 * Renders markdown content with elite formatting standards:
 * - Short paragraphs (2-3 sentences max)
 * - Proper heading hierarchy
 * - Visual emphasis on key phrases
 * - Scannable bullet points
 * - Styled callout boxes
 * - Markdown tables with proper styling
 */
export const MarkdownContentRenderer = ({ content }) => {
  if (!content) return null;

  // Extract tables using regex and replace with placeholders
  const tables = [];
  let processedContent = content;

  // Match markdown tables (header row, separator row, data rows)
  // Pattern: lines that start with | and continue until non-table content
  const tableRegex = /(?:^|\n)((?:\|[^\n]+\|\n?)+)/gm;

  processedContent = content.replace(tableRegex, (match, tableContent) => {
    // Check if it's a valid table (has header, separator, and at least one row)
    const lines = tableContent.trim().split('\n').filter(l => l.trim());
    if (lines.length >= 2 && lines[0].includes('|') && lines[1].includes('|')) {
      const tableIndex = tables.length;
      tables.push(tableContent.trim());
      return `\n\n__TABLE_${tableIndex}__\n\n`;
    }
    return match;
  });

  // Split content by double newlines to create natural sections
  const sections = processedContent.split('\n\n').filter(s => s.trim());

  // Helper: Parse inline markdown (bold, links)
  const parseInline = (text) => {
    if (!text) return null;

    // Handle links first: [text](url)
    let parts = [];
    let lastIndex = 0;
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let match;

    while ((match = linkRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      parts.push({ type: 'link', text: match[1], url: match[2] });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    // Now handle bold in each string part
    return parts.map((part, pIdx) => {
      if (typeof part === 'object' && part.type === 'link') {
        return (
          <a key={pIdx} href={part.url} className="text-primary hover:text-primary/80 underline transition">
            {part.text}
          </a>
        );
      }
      // Parse bold: **text**
      const boldParts = String(part).split(/\*\*(.*?)\*\*/g);
      return boldParts.map((bp, bpIdx) =>
        bpIdx % 2 === 1 ? (
          <span key={`${pIdx}-${bpIdx}`} className="font-semibold text-white">{bp}</span>
        ) : (
          <span key={`${pIdx}-${bpIdx}`}>{bp}</span>
        )
      );
    });
  };

  // Helper: Check if section is a table
  const isTable = (section) => {
    const lines = section.trim().split('\n');
    return lines.length >= 2 &&
      lines[0].includes('|') &&
      lines[1].includes('|') &&
      lines[1].includes('-');
  };

  // Helper: Parse markdown table
  const parseTable = (section) => {
    const lines = section.trim().split('\n').filter(l => l.trim());
    if (lines.length < 2) return null;

    // Parse header row
    const headerRow = lines[0].split('|').map(cell => cell.trim()).filter(c => c);

    // Skip separator row (line 1)
    // Parse data rows
    const dataRows = [];
    for (let i = 2; i < lines.length; i++) {
      const row = lines[i].split('|').map(cell => cell.trim()).filter(c => c);
      if (row.length > 0) {
        dataRows.push(row);
      }
    }

    return { headers: headerRow, rows: dataRows };
  };

  return (
    <div className="space-y-6 prose-invert max-w-none elite-article elite-text">
      {sections.map((section, idx) => {
        // Headers (h2, h3, h4)
        if (section.startsWith('### ')) {
          const headingText = section.replace('### ', '');
          return (
            <motion.h3
              key={idx}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="elite-h3 scroll-mt-20"
              id={headingText.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
            >
              {headingText}
            </motion.h3>
          );
        }

        if (section.startsWith('## ')) {
          const headingText = section.replace('## ', '');
          return (
            <motion.h2
              key={idx}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="elite-h2 scroll-mt-20"
              id={headingText.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
            >
              {headingText}
            </motion.h2>
          );
        }

        if (section.startsWith('# ')) {
          return null; // Skip H1 since article title is separate
        }

        // Horizontal rule
        if (section.trim() === '---') {
          return <hr key={idx} className="border-white/10 my-8" />;
        }

        // Table placeholders (from pre-processing)
        const tableMatch = section.match(/^__TABLE_(\d+)__$/);
        if (tableMatch) {
          const tableIndex = parseInt(tableMatch[1], 10);
          const tableContent = tables[tableIndex];
          if (!tableContent) return null;

          const table = parseTable(tableContent);
          if (!table) return null;

          return (
            <motion.div
              key={idx}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: idx * 0.05 }}
              className="my-6 overflow-x-auto rounded-lg border border-white/10"
            >
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-white/5 border-b border-white/10">
                    {table.headers.map((header, hIdx) => (
                      <th
                        key={hIdx}
                        className="px-4 py-3 text-left font-semibold text-white"
                      >
                        {parseInline(header)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row, rowIdx) => (
                    <tr
                      key={rowIdx}
                      className="border-b border-white/10 hover:bg-white/5 transition"
                    >
                      {row.map((cell, cellIdx) => (
                        <td
                          key={cellIdx}
                          className="px-4 py-3 text-muted-foreground"
                        >
                          {parseInline(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>
          );
        }

        // Markdown Tables
        if (isTable(section)) {
          const table = parseTable(section);
          if (!table) return null;

          return (
            <motion.div
              key={idx}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: idx * 0.05 }}
              className="my-6 overflow-x-auto rounded-lg border border-white/10"
            >
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-white/5 border-b border-white/10">
                    {table.headers.map((header, hIdx) => (
                      <th
                        key={hIdx}
                        className="px-4 py-3 text-left font-semibold text-white"
                      >
                        {parseInline(header)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row, rowIdx) => (
                    <tr
                      key={rowIdx}
                      className="border-b border-white/10 hover:bg-white/5 transition"
                    >
                      {row.map((cell, cellIdx) => (
                        <td
                          key={cellIdx}
                          className="px-4 py-3 text-muted-foreground"
                        >
                          {parseInline(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>
          );
        }

        // Bullet lists
        if (section.includes('\n-') || section.startsWith('-')) {
          const items = section.split('\n').filter(line => line.trim().startsWith('-'));
          return (
            <motion.ul
              key={idx}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: idx * 0.05 }}
              className="space-y-3 ml-0"
            >
              {items.map((item, itemIdx) => {
                const text = item.replace(/^-\s*/, '').trim();
                return (
                  <li key={itemIdx} className="flex gap-3 elite-body leading-relaxed">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-bold mt-0.5">
                      ✓
                    </span>
                    <span>{parseInline(text)}</span>
                  </li>
                );
              })}
            </motion.ul>
          );
        }

        // Callout boxes (marked with >)
        if (section.startsWith('>')) {
          const calloutText = section.replace(/^>\s*/, '').trim();
          return (
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="p-4 bg-primary/10 border-l-4 border-primary rounded-r-lg"
            >
              <p className="text-sm text-muted-foreground leading-relaxed">
                {parseInline(calloutText)}
              </p>
            </motion.div>
          );
        }

        // Regular paragraphs - break into readable chunks
        if (section.trim()) {
          return (
            <motion.p
              key={idx}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="elite-paragraph"
            >
              {parseInline(section)}
            </motion.p>
          );
        }

        return null;
      })}
    </div>
  );
};


/**
 * ProblemSection Component
 * Special formatting for problem/pain point sections
 */
export const ProblemSection = ({ title, items, description }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="my-8 p-6 bg-red-950/20 border-l-4 border-red-500/50 rounded-r-lg"
  >
    {title && <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>}
    {description && <p className="text-muted-foreground mb-4">{description}</p>}
    <ul className="space-y-3">
      {items?.map((item, idx) => (
        <li key={idx} className="flex gap-3 text-muted-foreground">
          <span className="flex-shrink-0 text-red-400 text-lg">❌</span>
          <span>
            {typeof item === 'string' ? item : item.title ? `${item.title}: ${item.description}` : item}
          </span>
        </li>
      ))}
    </ul>
  </motion.div>
);

/**
 * ComparisonTable Component
 * Styled table for feature comparisons
 */
export const ComparisonTable = ({ headers, rows, highlightColumn }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="my-8 overflow-x-auto"
  >
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="border-b border-white/10">
          {headers.map((header, idx) => (
            <th
              key={idx}
              className={`px-4 py-3 text-left font-semibold ${idx === highlightColumn
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground'
                }`}
            >
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIdx) => (
          <tr
            key={rowIdx}
            className={`border-b border-white/10 hover:bg-white/5 transition ${rowIdx === highlightColumn ? 'bg-primary/5' : ''
              }`}
          >
            {row.map((cell, cellIdx) => (
              <td
                key={cellIdx}
                className={`px-4 py-3 ${cellIdx === highlightColumn
                  ? 'text-primary font-medium'
                  : 'text-muted-foreground'
                  }`}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </motion.div>
);

/**
 * FeatureGrid Component
 * Display features in a scannable grid
 */
export const FeatureGrid = ({ features, columns = 2 }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className={`grid grid-cols-1 md:grid-cols-${columns} gap-4 my-8`}
  >
    {features.map((feature, idx) => (
      <motion.div
        key={idx}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: idx * 0.1 }}
        className="p-4 bg-white/5 border border-white/10 rounded-lg hover:border-primary/50 transition"
      >
        <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-400" />
          {feature.title}
        </h4>
        <p className="text-sm text-muted-foreground">{feature.description}</p>
      </motion.div>
    ))}
  </motion.div>
);

/**
 * CalloutBox Component
 * Pro tips, warnings, info boxes
 */
export const CalloutBox = ({ type = 'info', title, children }) => {
  const styles = {
    info: {
      bg: 'bg-blue-950/20',
      border: 'border-blue-500/50',
      icon: Info,
      textColor: 'text-blue-400'
    },
    warning: {
      bg: 'bg-yellow-950/20',
      border: 'border-yellow-500/50',
      icon: AlertCircle,
      textColor: 'text-yellow-400'
    },
    tip: {
      bg: 'bg-green-950/20',
      border: 'border-green-500/50',
      icon: Lightbulb,
      textColor: 'text-green-400'
    }
  };

  const style = styles[type] || styles.info;
  const Icon = style.icon;

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className={`my-6 p-4 ${style.bg} border-l-4 ${style.border} rounded-r-lg`}
    >
      {title && (
        <h4 className={`font-semibold ${style.textColor} mb-2 flex items-center gap-2`}>
          <Icon className="w-4 h-4" />
          {title}
        </h4>
      )}
      <div className="text-sm text-muted-foreground">{children}</div>
    </motion.div>
  );
};
